import { isCorrect } from 'helpers'
import { FC, useMemo } from 'react'
import { IMultiChoiceAnswered, IMultiChoiceUnanswered } from 'types/tests/sjl'

interface props {
  id: string
  number: number
  question: IMultiChoiceAnswered | IMultiChoiceUnanswered
  showResult?: boolean
}

const QuestionRenderer: FC<{question: string}> = ({question}) => {
  const element = useMemo(() => {
    const element = document.createElement("span")
    element.innerHTML = question
    // if has images replace
    const images = element.getElementsByTagName("img")
    Array.from(images).forEach((img) => {
      const wrapper = document.createElement("div")
      wrapper.className = "w-full bg-white flex justify-center py-5 relative pb-10 px-5 border-b border-b-slate-200"
      const image = document.createElement("img")
      image.className = "w-full mobile:max-w-none mobileWidth:max-w-none max-w-max"
      image.src = img.src
      wrapper.appendChild(image)
      const overlay = document.createElement("div")
      overlay.className = "absolute bg-back mix-blend-multiply inset-0"
      wrapper.appendChild(overlay)
      img?.replaceWith(wrapper)

    })
    return element.innerHTML
  }, [question])

  return <span dangerouslySetInnerHTML={{__html: element}} />
}

const choiceHTML = (choice: string, backdropClass: string) => {
    const element = document.createElement("span")
    element.innerHTML = choice
    const images = element.getElementsByTagName("img")
    Array.from(images).forEach((img) => {
      const wrapper = document.createElement("div")
      wrapper.className = "w-full bg-white relative py-4"
      const image = document.createElement("img")
      image.src = img.src
      wrapper.appendChild(image)
      const overlay = document.createElement("div")
      overlay.className = ["absolute mix-blend-multiply inset-0", backdropClass].asClass
      wrapper.appendChild(overlay)
      img?.replaceWith(wrapper)

    })
    return element.innerHTML
}

const MultichoiceQuestion: FC<props> = ({ id, number, question, showResult }) => {
  const correct = showResult && question.answered && isCorrect(question)

  const correctClass = showResult && 'text-green-600 border-green-600 border-2 bg-green-50'
  const incorrectClass = showResult && 'text-rose-600 border-rose-600 border-2 bg-rose-50'

  const numberClass = showResult
    ? correct
      ? 'ring-offset-2 ring-2 ring-green-600 rounded bg-green-600 w-8 shrink-0 text-center text-black select-none'
      : 'ring-offset-2 ring-2 ring-rose-600 rounded bg-rose-600 w-8 shrink-0 text-center text-black select-none'
    : 'ring-offset-2 ring-2 ring-jajko rounded bg-jajko w-8 shrink-0 text-center text-black select-none'

  return (
    <div
      style={{ borderLeft: !showResult ? undefined : correct ? '4px solid green' : '4px solid red' }}
      id={id}
      data-type="question"
            data-qtype={question.type}
data-number={number}
      className="flex flex-col w-full items-start space-x-2 px-5 justify-start pb-5"
    >
      <div className="flex items-start space-x-4 py-4">
        <span className={numberClass}>{number}</span>
        <QuestionRenderer question={question.question} />
      </div>
      {question.choices.map((choice: string, l: number) => (
        <div key={`${id}_${l}`} className="flex items-center mb-2 px-10 cursor-pointer">
          <input
            disabled={showResult}
            id={`${id}_${l}`}
            onInput={(e) => e.currentTarget.closest('[data-type="question"]')?.setAttribute('data-answer', e.currentTarget.checked ? e.currentTarget.value : '')}
            name={id}
            type="radio"
            className="peer disabled:hidden cursor-pointer h-4 w-4 mt-1 accent-kura shrink-0"
            value={l}
          />
          <label
            htmlFor={`${id}_${l}`}
            dangerouslySetInnerHTML={{ __html: choiceHTML(
              choice, 
              !showResult ? "bg-back" : correct && question.answer === l ?
              "bg-green-50" : !correct && question.answered && question.answer === l ?
              "bg-rose-50" : !correct && (question.correctAnswer as any).includes(l) ?
              "bg-green-50" : "bg-back"
            ) }}
            className={
              [
                'peer-checked:font-bold select-none cursor-pointer rounded px-2',
                !showResult
                  ? "peer-checked:text-kura"
                  : correct && question.answer === l
                  ? correctClass
                  : !correct && question.answered && question.answer === l
                  ? incorrectClass
                  : !correct && (question.correctAnswer as any).includes(l)
                  ? [correctClass, "border-0"].asClass
                  : undefined,
              ].asClass
            }
          />
        </div>
      ))}
    </div>
  )
}

export default MultichoiceQuestion
